/**
 * What the navigation offers a person. The sidebar and the command palette
 * both read NAV_ENTRIES (nav-data.ts) through these functions, so they cannot
 * disagree about which pages exist, which ones a role may open, or how many
 * times a page is listed.
 *
 * They used to: the sidebar filtered by permission and the palette did not,
 * so the palette offered screens the router then refused; and the palette
 * listed a section and its own first page as two commands for one page
 * ("CRM" and "Overview" both opened /app/crm).
 */
import { NAV_ENTRIES } from "./nav-data";

export type NavEntry = (typeof NAV_ENTRIES)[number];

/**
 * The entries a person with these permissions is offered.
 *
 * Children a role cannot open are dropped. A SECTION IS A CONTAINER, NOT A
 * GATE: filtering a section by its own permission first hid every child
 * inside it (an employee holds procurement.purchase_request.read and still
 * lost Purchase Requests, because the Procurement heading required something
 * else). A heading is visible when anything under it is, and hidden only when
 * nothing is. A leaf entry stands on its own permission.
 */
export function visibleNav(
  can: (permission: string) => boolean,
  entries: readonly NavEntry[] = NAV_ENTRIES,
): NavEntry[] {
  return entries
    .map((e) => ({
      ...e,
      children: e.children?.filter((c) => !c.permission || can(c.permission)),
    }))
    .filter((e) =>
      e.children ? e.children.length > 0 : !e.permission || can(e.permission),
    );
}

/**
 * Whether an entry leads only to pages that are not available yet: a page
 * marked `availability: "notYet"`, or a section every page of which is
 * (as shown, so after visibleNav). The sidebar and the palette tag it, so no
 * entry presents a page that runs nothing as working (REP-BUILDERS, D661).
 */
export function isNotAvailable(entry: {
  availability?: string;
  children?: ReadonlyArray<{ availability?: string }>;
}): boolean {
  if (entry.children && entry.children.length > 0)
    return entry.children.every((c) => c.availability === "notYet");
  return entry.availability === "notYet";
}

/** One page in the command palette. Labels are the English nav labels. */
export interface PaletteCommand {
  path: string;
  /** The page's name, from the first entry that lists it. */
  label: string;
  /** The page is not available yet; the palette says so beside its name. */
  notAvailable: boolean;
  /** The section listing it first; absent for a top-level page. */
  section?: string;
  /** Every name and section that lists this page, so each still finds it. */
  terms: string[];
}

/**
 * One command per page, in navigation order.
 *
 * A command is a page. A section heading is a container, as in the sidebar,
 * so it is not a command of its own: its path is always one of its pages
 * (CRM's is its Overview) or a redirect to one (Sales opens Quotations), and
 * listing it too put that page in the palette twice, or offered a page the
 * person's role does not include. Its pages are found by its name instead.
 * A page two sections list (Entity Builder, under Configuration and Setup)
 * is listed once, under the first, and found by either.
 */
export function paletteCommands(
  entries: readonly NavEntry[],
): PaletteCommand[] {
  const byPath = new Map<string, PaletteCommand>();
  const add = (
    label: string,
    path: string,
    notAvailable: boolean,
    section?: string,
  ) => {
    const names = section ? [label, section] : [label];
    const known = byPath.get(path);
    if (known) {
      for (const name of names)
        if (!known.terms.includes(name)) known.terms.push(name);
      return;
    }
    byPath.set(path, {
      path,
      label,
      notAvailable,
      ...(section ? { section } : {}),
      terms: names,
    });
  };
  for (const entry of entries) {
    if (entry.children) {
      for (const child of entry.children)
        add(child.label, child.path, isNotAvailable(child), entry.label);
    } else {
      add(entry.label, entry.path, isNotAvailable(entry));
    }
  }
  return [...byPath.values()];
}
